import { ElementName, ElementRole, PathSegment, TrackingData } from '../types';
import {
  isIdentityLike,
  isSensitiveText,
  resolveAccessibleName,
  toSegmentName,
} from './accessibility';

const MAX_SCOPE_SEGMENTS = 3;

const MAX_HEADING_DEPTH = 8;

const PAGE_DATA_KEYS = ['pageId', 'pageViewId'];

const SECTION_HEADING_SELECTOR = 'h2, h3, h4, h5, h6, [role="heading"]';

const DIALOG_HEADING_SELECTOR = 'h1, h2, h3, h4, h5, h6, [role="heading"]';

const NON_SECTION_CONTAINERS = ['BODY', 'MAIN', 'HTML'];

const SHELL_SELECTOR = 'header, main';

const DATA_PREFIX = 'data-track-data-';

export const toSegment = (role: string, name: string): PathSegment => ({
  role: role as ElementRole,
  name: (toSegmentName(name) || 'unnamed') as ElementName,
});

const parseSegment = (raw: string): PathSegment => {
  const separator = raw.indexOf('.');
  if (separator <= 0 || separator === raw.length - 1) {
    return toSegment('generic', raw);
  }
  return toSegment(raw.slice(0, separator), raw.slice(separator + 1));
};

const dialogName = (dialog: Element): string => {
  const labelled = dialog.getAttribute('aria-labelledby');
  if (labelled) {
    const doc = dialog.ownerDocument;
    const sources = labelled
      .split(/\s+/)
      .map(id => doc.getElementById(id))
      .filter((node): node is HTMLElement => !!node);
    const text = sources
      .map(node => node.textContent ?? '')
      .join(' ')
      .trim();
    if (text) {
      return sources.some(source => isSensitiveText(source, text)) ? '' : text;
    }
  }
  const label = dialog.getAttribute('aria-label');
  if (label) {
    return isIdentityLike(label) ? '' : label;
  }
  const heading = dialog.querySelector(DIALOG_HEADING_SELECTOR);
  const headingText = heading?.textContent?.trim() ?? '';
  return heading && isSensitiveText(heading, headingText) ? '' : headingText;
};

const ownedHeadingText = (container: Element, target: Element): string => {
  if (
    NON_SECTION_CONTAINERS.includes(container.tagName) ||
    container.querySelector(SHELL_SELECTOR)
  ) {
    return '';
  }
  const heading = container.querySelector(SECTION_HEADING_SELECTOR);
  if (
    !heading ||
    heading.contains(target) ||
    heading.querySelector('a, button')
  ) {
    return '';
  }
  const text = resolveAccessibleName(heading);
  return isSensitiveText(heading, text) ? '' : text;
};

const readDataAttributes = (element: Element, into: TrackingData): void => {
  for (const attribute of Array.from(element.attributes)) {
    if (!attribute.name.startsWith(DATA_PREFIX)) {
      continue;
    }
    const suffix = attribute.name.slice(DATA_PREFIX.length);
    const key = suffix.replace(/-([a-z0-9])/g, (_, char: string) =>
      char.toUpperCase(),
    );
    if (PAGE_DATA_KEYS.includes(key) || key in into) {
      continue;
    }
    into[key] = attribute.value;
  }
};

export const capScope = (segments: PathSegment[]): PathSegment[] => {
  if (segments.length <= MAX_SCOPE_SEGMENTS) {
    return segments;
  }
  return [segments[0], ...segments.slice(-(MAX_SCOPE_SEGMENTS - 1))];
};

export type ResolvedScope = {
  segments: PathSegment[];
  data: TrackingData;
  declaredNames: ReadonlySet<string>;
};

export const resolveScope = (host: Element): ResolvedScope => {
  const segments: PathSegment[] = [];
  const declaredNames = new Set<string>();
  const data: TrackingData = {};
  readDataAttributes(host, data);
  let headingTaken = false;
  let dialogTaken = false;
  let depth = 0;
  let node: Element | null = host.parentElement;

  while (node) {
    readDataAttributes(node, data);
    depth += 1;
    const declared = node.getAttribute('data-track-scope');
    if (declared) {
      const segment = parseSegment(declared);
      dialogTaken = dialogTaken || segment.role === 'dialog';
      declaredNames.add(segment.name);
      segments.unshift(segment);
    } else if (node.getAttribute('role') === 'dialog') {
      if (!dialogTaken) {
        dialogTaken = true;
        segments.unshift(toSegment('dialog', dialogName(node)));
      }
    } else if (node.getAttribute('data-component') === 'ActionBar') {
      segments.unshift(toSegment('toolbar', 'actions'));
    } else if (
      !headingTaken &&
      segments.length === 0 &&
      depth <= MAX_HEADING_DEPTH
    ) {
      const heading = ownedHeadingText(node, host);
      if (heading) {
        headingTaken = true;
        segments.unshift(toSegment('region', heading));
      }
    }
    node = node.parentElement;
  }

  return { segments, data, declaredNames };
};
